import { getOauthClientId } from '@local/app-config';
import {
    fetchBaseQuery,
    type BaseQueryApi,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import {
    DEV_HUB_URL_STRING,
    GLOBAL_DISCOVERY_DEVELOPMENT_URL,
    GLOBAL_DISCOVERY_INTEGRATION_URL,
    LOGIN_SILENT,
    LOGIN_SUCCESS_ROUTE,
} from '../../../constants';
import {
    AUTH_CODE_KEY,
    getCombinedToken as getAccessToken,
    setCombinedToken as setAccessToken,
} from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getPkceVerifier } from '../../../store/sessionStorageHelpers/pkceFlowHelpers';
import type { AccessAndIDTokenResponse as AccessTokenResponse } from '../../types';
import { BentleyIdentityApiClient } from '../BentleyIdentityApiClient';
import { isFetchArgs, mergeResults } from './BentleyAccessTokenApiUtils';

export function accessTokenBody(
    code: string,
    forceOauthPrefix: boolean,
    tabId?: string,
    extendSession?: boolean,
) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const redirectUrl = extendSession
        ? `${window.location.origin}${LOGIN_SILENT}`
        : `${window.location.origin}${LOGIN_SUCCESS_ROUTE}`;

    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('code', code);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', redirectUrl);
    urlEncodedBody.append('client_id', clientId);
    const verifier = getPkceVerifier(tabId);
    urlEncodedBody.append('code_verifier', verifier);

    return urlEncodedBody;
}

export function refreshTokenBody(refreshToken: string, forceOauthPrefix: boolean) {
    const clientId = getOauthClientId(forceOauthPrefix);

    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('grant_type', 'refresh_token');
    urlEncodedBody.append('refresh_token', refreshToken);
    urlEncodedBody.append('client_id', clientId);

    return urlEncodedBody;
}

interface AccessTokenParams {
    authCode: string;
    forceOauthPrefix: boolean;
    tabId?: string;
    extendSession?: boolean;
}
interface RefreshAccessTokenParams extends Omit<AccessTokenParams, 'authCode'> {
    refreshToken: string;
}

const tokenMutex = new Mutex();
export async function processResult(
    currentArgs: string | FetchArgs,
    WebApi: BaseQueryApi,
    extraOptions: any,
    forceOauthPrefix: boolean,
    url: string,
    discoveryIntegrationUrl?: string,
    hubUrl?: string,
) {
    const rawBaseQuery = fetchBaseQuery({
        baseUrl: url,
        prepareHeaders: (headers) => {
            const token = getAccessToken()?.access_token;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    });
    const rawBaseQueryIntegration = discoveryIntegrationUrl
        ? fetchBaseQuery({
              baseUrl: discoveryIntegrationUrl,
              prepareHeaders: (headers) => {
                  const token = getAccessToken()?.access_token;
                  if (token) {
                      headers.set('authorization', `Bearer ${token}`);
                  }
                  return headers;
              },
          })
        : undefined;
    // We have a mutex to prevent fetching a token multiple times if a number of calls happen at once.
    await tokenMutex.waitForUnlock();
    let result = rawBaseQueryIntegration
        ? await mergeResults(
              currentArgs,
              WebApi,
              extraOptions,
              rawBaseQuery,
              rawBaseQueryIntegration,
              hubUrl,
          )
        : await rawBaseQuery(currentArgs, WebApi, extraOptions);
    // If the result errors out with a 401, we probably have an expired access token.
    const originalToken = getAccessToken();
    if (result.error && result.error.status === 401 && originalToken) {
        const refreshToken = originalToken?.refresh_token ?? false;
        if (!refreshToken) {
            // Return the error, we don't have enough information to get a new one.
            return result;
        }
        if (!tokenMutex.isLocked()) {
            // Actually fetch the new access token, forcing a refetch that
            // subscribes to result to make sure the request is completed
            const refreshResult = await WebApi.dispatch(
                AccessTokenApi.endpoints.refreshBentleyAccessToken.initiate(
                    {
                        forceOauthPrefix,
                        refreshToken,
                    },
                    { subscribe: true, forceRefetch: true },
                ),
            );

            // If we actually got a new token, try the original query again.
            const newToken = getAccessToken();
            if (refreshResult?.isSuccess && originalToken.access_token !== newToken?.access_token) {
                // retry the initial query
                result = rawBaseQueryIntegration
                    ? await mergeResults(
                          currentArgs,
                          WebApi,
                          extraOptions,
                          rawBaseQuery,
                          rawBaseQueryIntegration,
                          hubUrl,
                      )
                    : await rawBaseQuery(currentArgs, WebApi, extraOptions);
            }
        } else {
            // There is another query fetching a token. Wait for that, and try query again assuming we have a new
            // token now.
            await tokenMutex.waitForUnlock();
            result = rawBaseQueryIntegration
                ? await mergeResults(
                      currentArgs,
                      WebApi,
                      extraOptions,
                      rawBaseQuery,
                      rawBaseQueryIntegration,
                      hubUrl,
                  )
                : await rawBaseQuery(currentArgs, WebApi, extraOptions);
        }
    }
    return result;
}

export const AccessTokenApi = BentleyIdentityApiClient.injectEndpoints({
    endpoints: (build) => ({
        retrieveBentleyAccessToken: build.query<AccessTokenResponse, AccessTokenParams>({
            async queryFn(
                { authCode, forceOauthPrefix, tabId, extendSession },
                _api,
                _extraOptions,
                baseQuery,
            ) {
                const accessTokenResponse = await baseQuery({
                    url: 'connect/token',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: accessTokenBody(authCode, forceOauthPrefix, tabId, extendSession),
                });
                if (accessTokenResponse.error) {
                    return { error: accessTokenResponse.error };
                }
                setAccessToken(accessTokenResponse.data as AccessTokenResponse);
                sessionStorage.setItem(AUTH_CODE_KEY, authCode);
                return { data: accessTokenResponse.data as AccessTokenResponse };
            },
        }),
        refreshBentleyAccessToken: build.query<AccessTokenResponse, RefreshAccessTokenParams>({
            async queryFn({ forceOauthPrefix, refreshToken }, _api, _extraOptions, baseQuery) {
                const accessTokenResponse = await baseQuery({
                    url: 'connect/token',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: refreshTokenBody(refreshToken, forceOauthPrefix),
                });
                if (accessTokenResponse.error) {
                    return { error: accessTokenResponse.error };
                }
                setAccessToken(accessTokenResponse.data as AccessTokenResponse);
                return { data: accessTokenResponse.data as AccessTokenResponse };
            },
        }),
    }),
});

export interface UrlConfigType extends Omit<AccessTokenParams, 'authCode'> {
    url?: string;
}
export type UrlConfigCallback = () => UrlConfigType | null;

export function createBentleyAccessTokenBaseQuery(
    urlConfig: UrlConfigCallback,
    transformedFilterParams?: (args: FetchArgs) => FetchArgs,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
    return async (args, WebApi, extraOptions) => {
        const urlConfiguration = urlConfig();
        if (urlConfiguration === null || !urlConfiguration.url) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }
        const { url: baseUrl, forceOauthPrefix } = urlConfiguration;

        let currentArgs;
        if (transformedFilterParams) {
            currentArgs = transformedFilterParams(args as FetchArgs);
        } else {
            currentArgs = args;
        }

        let hubUrl = '';
        let combineResponses = false;
        if (isFetchArgs(currentArgs) && 'params' in currentArgs && currentArgs.params) {
            const { combineResponses: cr, hubUrl: hu, ...restParams } = currentArgs.params;
            combineResponses = !!cr;
            hubUrl = hu ?? '';
            currentArgs = {
                ...currentArgs,
                params: Object.keys(restParams).length > 0 ? restParams : undefined,
            };
        }
        if (combineResponses) {
            let developmentDiscoveryUrl, integrationDiscoveryUrl;
            if (
                baseUrl.includes(DEV_HUB_URL_STRING) ||
                baseUrl === GLOBAL_DISCOVERY_DEVELOPMENT_URL
            ) {
                developmentDiscoveryUrl = baseUrl;
                integrationDiscoveryUrl = GLOBAL_DISCOVERY_INTEGRATION_URL;
            } else {
                developmentDiscoveryUrl = GLOBAL_DISCOVERY_DEVELOPMENT_URL;
                integrationDiscoveryUrl = baseUrl;
            }
            return processResult(
                currentArgs,
                WebApi,
                extraOptions,
                forceOauthPrefix,
                developmentDiscoveryUrl,
                integrationDiscoveryUrl,
                hubUrl,
            );
        }
        return processResult(currentArgs, WebApi, extraOptions, forceOauthPrefix, baseUrl);
    };
}

export const {
    useRetrieveBentleyAccessTokenQuery,
    useLazyRetrieveBentleyAccessTokenQuery,
    useLazyRefreshBentleyAccessTokenQuery,
} = AccessTokenApi;
